import React, {useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {Dialog} from '@material-ui/core';
import {Form, Formik} from 'formik';
import cn from 'classnames';
import DialogContainer from '../../../../../components/DialogContainer/styles';
import DialogTitle from '../../../../../components/DialogTitle/styles';
import {DialogBody, FieldInput} from '../../../dashboard/styles';
import DialogFields from '../../../../../components/DialogFields/styles';
import DialogField from '../../../../../components/DialogField/styles';
import {
  ErrorSign,
  ErrorSignWrapper
} from '../../../../../components/errorSign/errorSign';
import DialogActions from '../../../../../components/DialogActions/styles';
import DialogAction from '../../../../../components/DialogAction/styles';
import {ButtonOutline, ButtonPrimary} from '../../../../../components/buttons';
import {FieldLabel} from '../../../../../components/FieldLabel/styles';
import {
  CREATE_FEATURE,
  OPEN_CREATE_FEATURE_MODAL,
  UPDATE_FEATURE
} from '../../../../../store/featureManagement/action';
import {
  FeatureFlagsCreateSchema,
  FeatureFlagsEditSchema
} from '../../../../../helpers/services/formValidationSchemas/FeatureFlagsSchema';
import SwitchS2E from '../../../../../components/Switch';
import FieldTextarea from '../../../../../components/FieldTextarea';

const getCreateInitialValues = () => ({
  name: '',
  route: null,
  showOnTablet: false,
  isNewFeature: true,
  icon: null,
  enabled: false,
  description: ''
});

const getEditInitialValues = (feature) => ({
  id: feature.id,
  name: feature.name || '',
  route: feature.route || null,
  showOnTablet: feature.showOnTablet ?? false,
  icon: feature.icon || null,
  description: feature.description || ''
});

const CreateFeatureModal = ({isOpen}) => {
  const dispatch = useDispatch();
  const formikRef = useRef();
  const editingFeature = useSelector(
    (state) => state.featureManagementReducer.editingFeature
  );
  const isEditMode = Boolean(editingFeature?.id);

  const closeModal = () => {
    dispatch(OPEN_CREATE_FEATURE_MODAL(false));
  };

  const initialValues = isEditMode
    ? getEditInitialValues(editingFeature)
    : getCreateInitialValues();

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      innerRef={formikRef}
      validationSchema={
        isEditMode ? FeatureFlagsEditSchema : FeatureFlagsCreateSchema
      }
      onSubmit={(values, {resetForm}) => {
        const body = {...values};

        if (!body.route) {
          body.route = null;
        }
        if (!body.icon) {
          body.icon = null;
        }

        if (isEditMode) {
          dispatch(
            UPDATE_FEATURE({
              feature: values.id,
              data: {
                route: body.route,
                icon: body.icon,
                showOnTablet: body.showOnTablet,
                description: body.description
              },
              callBack: () => {
                closeModal();
                resetForm();
              }
            })
          );
          return;
        }

        body.id = body.name
          .toLowerCase()
          .replace(/\s+/g, '_')
          .replace(/[_]{2,}/g, '_');
        dispatch(CREATE_FEATURE(body));
        closeModal();
        resetForm();
      }}
    >
      {({values, handleChange, setFieldValue, errors, touched}) => {
        return (
          <Dialog
            open={isOpen}
            onClose={() => {
              closeModal();
            }}
            scroll='body'
          >
            <DialogContainer className='DialogMdPadding'>
              <DialogTitle className='DialogTitleMarginBottom'>
                {isEditMode ? 'Edit feature flag' : 'Add new feature flag'}
              </DialogTitle>

              <DialogBody>
                <Form>
                  <DialogFields>
                    <DialogField className='w100'>
                      <FieldLabel
                        className={cn('FieldLabelLg modalLabel', {
                          error: errors.name && touched.name && 'error'
                        })}
                      >
                        Name <span>*</span>
                      </FieldLabel>
                      <FieldInput
                        className={cn({
                          error: errors.name && touched.name,
                          disabled: isEditMode
                        })}
                        name='name'
                        value={values.name}
                        placeholder='Name'
                        onChange={handleChange}
                        disabled={isEditMode}
                        onBlur={(e) => {
                          if (!isEditMode) {
                            setFieldValue('name', e.target.value?.trim());
                          }
                        }}
                      />
                      <ErrorSignWrapper className='positionRight2'>
                        {errors.name && touched.name && (
                          <ErrorSign>{errors.name}</ErrorSign>
                        )}
                      </ErrorSignWrapper>
                    </DialogField>
                  </DialogFields>
                  <DialogFields>
                    <DialogField className='w100'>
                      <FieldLabel
                        className={cn('FieldLabelLg modalLabel', {
                          error:
                            errors.description && touched.description && 'error'
                        })}
                      >
                        Description <span>*</span>
                      </FieldLabel>
                      <FieldTextarea
                        type='text'
                        className={cn('FieldTextareaMd', {
                          error: errors.description && touched.description
                        })}
                        name='description'
                        placeholder='Description'
                        value={values.description}
                        onChange={handleChange}
                        onBlur={(e) => {
                          setFieldValue('description', e.target.value?.trim());
                        }}
                      />
                      <ErrorSignWrapper className='positionRight2'>
                        {errors.description && touched.description && (
                          <ErrorSign>{errors.description}</ErrorSign>
                        )}
                      </ErrorSignWrapper>
                    </DialogField>
                  </DialogFields>
                  <DialogFields>
                    <DialogField className='w100'>
                      <FieldLabel
                        className={cn('FieldLabelLg modalLabel', {
                          error: errors.route && touched.route && 'error'
                        })}
                      >
                        Route
                      </FieldLabel>
                      <FieldInput
                        className={errors.route && touched.route && 'error'}
                        name='route'
                        value={values.route || ''}
                        placeholder='Route'
                        onChange={handleChange}
                      />
                      <ErrorSignWrapper className='positionRight2'>
                        {errors.route && touched.route && (
                          <ErrorSign>{errors.route}</ErrorSign>
                        )}
                      </ErrorSignWrapper>
                    </DialogField>
                    <DialogField className='w100'>
                      <FieldLabel className='FieldLabelLg modalLabel'>
                        Icon
                      </FieldLabel>
                      <FieldInput
                        name='icon'
                        value={values.icon || ''}
                        placeholder='Icon'
                        onChange={handleChange}
                      />
                    </DialogField>
                    {!isEditMode && (
                      <>
                        <DialogField>
                          <SwitchS2E
                            onChange={handleChange}
                            name='enabled'
                            checked={values.enabled}
                            label='Enabled feature'
                            labelPlacement='start'
                          />
                        </DialogField>
                        <DialogField>
                          <SwitchS2E
                            onChange={handleChange}
                            name='isNewFeature'
                            checked={values.isNewFeature}
                            label='New feature mark'
                            labelPlacement='start'
                          />
                        </DialogField>
                      </>
                    )}
                    <DialogField>
                      <SwitchS2E
                        onChange={handleChange}
                        name='showOnTablet'
                        checked={values.showOnTablet}
                        label='Show on tablet'
                        labelPlacement='start'
                      />
                    </DialogField>
                  </DialogFields>
                  <DialogActions className='justifyCenter'>
                    <DialogAction className='w40'>
                      <ButtonOutline
                        type='button'
                        onClick={() => {
                          closeModal();
                        }}
                      >
                        <span>Cancel</span>
                      </ButtonOutline>
                    </DialogAction>
                    <DialogAction className='w40'>
                      <ButtonPrimary type='submit'>
                        <span>{isEditMode ? 'Save' : 'Submit'}</span>
                      </ButtonPrimary>
                    </DialogAction>
                  </DialogActions>
                </Form>
              </DialogBody>
            </DialogContainer>
          </Dialog>
        );
      }}
    </Formik>
  );
};

CreateFeatureModal.propTypes = {
  isOpen: PropTypes.bool.isRequired
};
export default CreateFeatureModal;
